import { Box, FlexBox, Icon, Typography, getTokenCssVar } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { PRINT_OPTIONS_CHOICE_DESIGNER } from './types';
import type { PrintOptionsReply } from './parseReply';
import type { PrintOptionSelectionDetail } from './types';

type PrintOptionsMessageViewProps = {
    data: PrintOptionsReply;
};

const THUMBNAIL_SIZE = '32px';

/**
 * The settled, read-only answer for a print-options turn, shown inside the customer's own
 * message bubble.
 *
 * No border, matching `StyleTilesMessageView` and the composer band: the pilot's card asks for
 * `1px solid var(--swan-sem-color-border-subtle)`, a custom property SWAN has never defined, so
 * the declaration is invalid and dropped and no border has ever rendered in production.
 * Reproducing the dead token would only carry the bug forward.
 */
export function PrintOptionsMessageView({ data }: PrintOptionsMessageViewProps) {
    if (data.choice === PRINT_OPTIONS_CHOICE_DESIGNER) {
        return (
            <Card>
                <FlexBox alignItems="center" gap={3} paddingY={4} paddingX={5}>
                    <Icon iconType="workWithAPro" size="standard" />
                    <Typography fontSkin="body-standard-bold">
                        {__('Let my designer decide')}
                    </Typography>
                </FlexBox>
            </Card>
        );
    }

    // `selectionDetails` carries the localised names resolved when the answer was sent. Rows are
    // derived from the raw `selections` map only as a fallback, for sessions persisted before
    // details were recorded — those render attribute keys, which is worse than a name but far
    // better than an empty card.
    const details: PrintOptionSelectionDetail[] =
        data.selectionDetails ??
        Object.entries(data.selections ?? {}).map(([optionName, valueName]) => ({
            optionName,
            valueName,
        }));

    return (
        <Card>
            <FlexBox flexDirection="column" gap={3} paddingY={4} paddingX={5}>
                <FlexBox alignItems="center" gap={3}>
                    <Icon iconType="printedProducts" size="standard" />
                    <Typography fontSkin="body-standard-bold">
                        {__('I know my product specs')}
                    </Typography>
                </FlexBox>
                {details.map((detail) => (
                    <FlexBox key={detail.optionName} alignItems="center" gap={3}>
                        {detail.imageUrl && (
                            <img
                                src={detail.imageUrl}
                                alt={detail.valueName}
                                style={{
                                    width: THUMBNAIL_SIZE,
                                    height: THUMBNAIL_SIZE,
                                    borderRadius: getTokenCssVar('SwanSemBorderRadiusSubtle'),
                                    objectFit: 'cover',
                                }}
                            />
                        )}
                        <Typography fontSkin="body-standard">
                            <Typography fontSkin="body-standard-bold" as="span">
                                {detail.optionName}:
                            </Typography>{' '}
                            {detail.valueName}
                        </Typography>
                    </FlexBox>
                ))}
            </FlexBox>
        </Card>
    );
}

function Card({ children }: { children: React.ReactNode }) {
    return (
        <Box
            backgroundColor="standard"
            style={{
                borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
                overflow: 'hidden',
            }}
        >
            {children}
        </Box>
    );
}
